import React, { useState, useEffect } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { t } from '../../../infrastructure/i18nextHelper'
import { defaultStyles, muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { api } from '../../../infrastructure/api'
import guid, { Guid } from '../../../infrastructure/guid'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { ColumnDescriptor, Select, TableItem, DatePicker } from '../../common/customComponents'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { LocalProduct } from '../masterData/localProduct'
import { Item } from '../../common/components/select/types'
import { PriceIndex } from '../../pricings/pricingModels'
import { hasFeature } from '../../../infrastructure/feature'
import { UserSettingsContextContainer } from '../../../infrastructure/shell/userSettings/userSettingsStore'
import moment from 'moment'

function PricingPeriodMasterData({ classes }: MuiProps) {
    let excelGenerator = ExcelGeneratorContainer.useContainer()

    let [priceIndexs, setPriceIndexs] = useState<PriceIndex[]>([])
    let [products, setProducts] = useState<ProductChannels[]>([])
    let [pricingPeriods, setPricingPeriods] = useState<TableItem<PricingPeriod>[]>([])      
    let [filters, setFilters] = useState<PricingPeriodFilters>(noFilters)
    let [localProducts, setLocalProducts] = useState<LocalProduct[]>([])
    let [pricingPeriodRules, setPricingPeriodRules] = useState<PricingPeriodRule[]>([])

    const productChannels = (productId: Guid) => products.find(x => x.productId === productId)?.channels ?? []
    const channelCustomerSegments = (productId: Guid, channel: string) =>
        products.find(x => x.productId === productId)?.channels?.find(x => x.name === channel)?.customerSegments ?? []

    const tBase = 'admin.masterdata.pricingPeriod.'

    const movementDates: MovementDate[] = ['FirstFortnight', 'SecondFortnight', 'FullMonth', 'Current15ToNext14', 'RefBy4Weeks',
        'Current1To10', 'Current11To20', 'Current21ToEndOfMonth', 'RefBy3CD', 'RefBy3CD4CD', 'RefBy7CD']
    const movementDateItems: Item[] =
        movementDates.map(x => { return { value: x as MovementDate, text: t(`${tBase}movementDate.${x}`) } })

    const pricingFormulas: PricingFormula[] = ['Last13ToLast27', 'Last28To12', 'FullMonth', 'LastFullMonth', 'SecondLastFullMonth',
        'SecondLast25ToLast24', 'RefBy4Weeks', 'ThirdLastFullMonth', 'Last21ToLastEndOfMonth', 'Current1To10', 'Current11To20',
        'LastSecondFortnight', 'FirstFortnight', 'SecondFortnight', 'Current21ToEndOfMonth', 'Last16ToCurrent15', 'SecondLast28ToLast27', 'RefBy3CD',
        'RefBy7CD', 'DynamicPricing']
    const pricingFormulaItems: Item[] = pricingFormulas
        .filter(x => hasFeature('DynamicPricing') || x !== 'DynamicPricing')
        .map(x => { return { value: x, text: t(`${tBase}pricingFormula.${x}`) } })

    let settings = UserSettingsContextContainer.useContainer()

    let load = async () => {
        let priceIndexPromise = api.get<PriceIndex[]>('masterdata/pricingPeriod/priceIndex')
        let productPromise = api.get<ProductChannels[]>('masterdata/pricingPeriod/productChannelCustomerSegment')
        let pricingPeriodPromise = api.get<TableItem<PricingPeriod>[]>('masterdata/pricingPeriod')
        let localProductPromise = api.get<LocalProduct[]>('masterdata/pricingPeriod/localProduct')
        let pricingPeriodRulePromise = api.get<PricingPeriodRule[]>('masterdata/pricingPeriod/pricingPeriodRule')

        setPricingPeriods(await pricingPeriodPromise)
        setPriceIndexs(await priceIndexPromise)
        setProducts(await productPromise)
        setLocalProducts(await localProductPromise)
        setPricingPeriodRules(await pricingPeriodRulePromise)
    }

    let getPriceIndexs = (productId: string) => {
        let productPriceIndexs = localProducts.find(x => x.productId == productId)?.priceIndexs
        return priceIndexs.filter(x => productPriceIndexs?.find(i => i == x.medecoCode))
    }

    let getItems = () => applyFilters(pricingPeriods, filters, pricingPeriodRules).map(toTableItem)

    useEffect(() => {
        load()
    }, [])

    let onSave = async (item: PricingPeriod) => {
        await api.post('masterdata/pricingPeriod', item)
        await load()
        return true
    }

    let onDelete = async (ids: string[]) => {
        if (ids.length === 0) return false
        await api.del('masterdata/pricingPeriod', { ids })
        await load()
        return true
    }

    let exportExcel = () => {
        excelGenerator.generate({
            filename: 'PricingPeriod.xlsx',
            sheets: [{ name: 'PricingPeriod', lines: createExcelLines(getItems(), columns) }]
        })
    }

    let columns: ColumnDescriptor<TableItem<MasterDataItem<PricingPeriod>>>[] = [
        {
            name: t(tBase + 'product'),
            value: x => x.product,
            columnFilter: { value: filters.product ?? '', onChange: (product: string) => setFilters({ ...filters, product }) }
        },
        {
            name: t(tBase + 'channel'),
            value: x => x.channel,
            columnFilter: { value: filters.channel ?? '', onChange: (channel: string) => setFilters({ ...filters, channel }) }
        },
        {
            name: t(tBase + 'customerSegment'),
            value: x => x.customerSegment,
            columnFilter: {
                value: filters.customerSegment ?? '',
                onChange: (customerSegment: string) => setFilters({ ...filters, customerSegment })
            }
        },
        {
            name: t(tBase + 'finalPricingFormulaIndex'),
            value: x => x.finalPricingFormulaIndexName,
            columnFilter: {
                value: filters.finalPricingFormulaIndexName ?? '',
                onChange: (finalPricingFormulaIndexName: string) => setFilters({ ...filters, finalPricingFormulaIndexName })
            }
        },
        {
            name: t(tBase + 'movementDate.label'),
            value: x => t(`${tBase}movementDate.${x.movementDate}`).toString(),
            columnFilter: {
                value: filters.movementDate ?? '',
                onChange: (movementDate: string) => setFilters({ ...filters, movementDate })
            }
        },
        {
            name: t(tBase + 'pricingFormula.label'),
            value: x => t(`${tBase}pricingFormula.${x.pricingFormula}`).toString(),
            columnFilter: {
                value: filters.pricingFormula ?? '',
                onChange: (pricingFormula: string) => setFilters({ ...filters, pricingFormula })
            }
        },
        {
            name: t(tBase + 'formulaType.label'),
            hidden: !hasFeature('FormulaType'),
            value: x => x.formulaType ? t(`${tBase}formulaType.${x.formulaType}`).toString() : '',
            columnFilter: {
                value: filters.formulaType ?? '',
                onChange: (formulaType: string) => setFilters({ ...filters, formulaType })
            }
        },
        {
            name: t(tBase + 'pricingPeriodRule'),
            value: x => getPricingPeriodRuleName(x.pricingPeriodRuleId),
            hidden: !hasFeature('DynamicPricing'),
            columnFilter: {
                value: filters.pricingPeriodRule ?? '',
                onChange: (pricingPeriodRule: string) => setFilters({ ...filters, pricingPeriodRule })
            }
        },
        {
            name: t(tBase + 'deemedDate'),
            value: x => formatDate(x.deemedDate),
            hidden: !hasFeature('DynamicPricing')
        },
        {
            name: t(tBase + 'refMovementDate'),
            value: x => formatDate(x.refMovementDate)
        },
        {
            name: t(tBase + 'refPricingDate'),
            value: x => formatDate(x.refPricingDate)
        }
    ]

    let isManager = hasClaim(Claims.MasterdataPricingPeriodManager)
    let formatDate = (date: string | null) => date ? moment(date).format(settings.getDateFormat()) : ''
    let getPricingPeriodRuleName = (id: Guid | null) => pricingPeriodRules.find(x => x.id === id)?.pricingPeriod ?? ''

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId={'pricing-period-table'}
                headerLabel={t(tBase + 'marketingSales')}
                itemLabel={t(tBase + 'marketingSale')}
                isManager={isManager}
                onExportExcel={exportExcel}
                onNew={emptyPricingPeriod}
                onDelete={onDelete}
                onSave={onSave}
                items={getItems()}
                columns={columns}>{
                    (selectedItem, setSelectedItem) => (
                        <>
                            <Select label={t(tBase + 'product')}
                                disabled={!isManager}
                                value={selectedItem.productId}
                                choices={products.map(x => ({ value: x.productId, text: x.code }))}
                                onChange={productId => {
                                    if (!productId) return
                                    const channel = productChannels(productId).find(x => x === selectedItem.channel)?.name
                                        || productChannels(productId)[0].name
                                    let priceIndex = getPriceIndexs(productId).find(x => x.medecoCode === selectedItem.finalPricingFormulaIndex)?.medecoCode
                                        || getPriceIndexs(productId)[0]?.medecoCode
                                    setSelectedItem({ ...selectedItem, productId, channel, finalPricingFormulaIndex: priceIndex })
                                }} />
                            <Select label={t(tBase + 'channel')}
                                disabled={!isManager}
                                value={selectedItem.channel}
                                choices={productChannels(selectedItem.productId).map(x => ({ value: x.name, text: x.name }))}
                                onChange={channel => {
                                    if (!channel) return
                                    setSelectedItem({ ...selectedItem, channel, customerSegment: null })
                                }} />
                            <Select label={t(tBase + 'customerSegment')}
                                isClearable={true}
                                clearableLabel='None'
                                disabled={!isManager}
                                value={selectedItem.customerSegment}
                                choices={channelCustomerSegments(selectedItem.productId, selectedItem.channel)
                                    .map(x => ({ value: x, text: x }))}
                                onChange={customerSegment => {
                                    setSelectedItem({ ...selectedItem, customerSegment })
                                }} />
                            <Select label={t(tBase + 'finalPricingFormulaIndex')}
                                disabled={!isManager}
                                value={selectedItem.finalPricingFormulaIndex}
                                choices={getPriceIndexs(selectedItem.productId).map(x => ({ value: x.medecoCode, text: x.name }))}
                                onChange={finalPricingFormulaIndex => {
                                    if (finalPricingFormulaIndex) setSelectedItem({ ...selectedItem, finalPricingFormulaIndex })
                                }} />
                            <Select label={t(tBase + 'movementDate.label')}
                                disabled={!isManager}
                                value={selectedItem.movementDate}
                                choices={movementDateItems}
                                onChange={movementDate => { if (movementDate) setSelectedItem({ ...selectedItem, movementDate }) }} />
                            <Select label={t(tBase + 'pricingFormula.label')}
                                disabled={!isManager}
                                value={selectedItem.pricingFormula}
                                choices={pricingFormulaItems}
                                onChange={pricingFormula => {
                                    let pricingPeriodRuleId = pricingFormula === 'DynamicPricing' ? selectedItem.pricingPeriodRuleId : null
                                    let deemedDate = pricingFormula === 'DynamicPricing' ? selectedItem.deemedDate : null
                                    if (pricingFormula) setSelectedItem({ ...selectedItem, pricingFormula, pricingPeriodRuleId, deemedDate })
                                }} />
                            {hasFeature('FormulaType') && <Select label={t(tBase + 'formulaType.label')}
                                isClearable={true}
                                clearableLabel='None'
                                disabled={!isManager}
                                value={selectedItem.formulaType}
                                choices={getFormulaTypes()}
                                onChange={formulaType => setSelectedItem({ ...selectedItem, formulaType })} />}
                            {hasFeature('DynamicPricing') && selectedItem.pricingFormula === 'DynamicPricing' &&
                                <>
                                    <Select label={t(tBase + 'pricingPeriodRule')}
                                        isClearable={true}
                                        clearableLabel='None'
                                        disabled={!isManager}
                                        value={selectedItem.pricingPeriodRuleId}
                                        choices={pricingPeriodRules.map(x => ({ value: x.id, text: x.pricingPeriod }))}
                                        onChange={pricingPeriodRuleId => setSelectedItem({ ...selectedItem, pricingPeriodRuleId })} />
                                    <DatePicker label={t(tBase + 'deemedDate')}
                                        disabled={!isManager}
                                        date={selectedItem.deemedDate}
                                        setDate={deemedDate => setSelectedItem({ ...selectedItem, deemedDate })} />
                                </>
                            }
                            <DatePicker label={t(tBase + 'refMovementDate')}
                                disabled={!isManager}
                                date={selectedItem.refMovementDate}
                                setDate={refMovementDate => setSelectedItem({ ...selectedItem, refMovementDate })} />
                            <DatePicker label={t(tBase + 'refPricingDate')}
                                disabled={!isManager}
                                date={selectedItem.refPricingDate}
                                setDate={refPricingDate => setSelectedItem({ ...selectedItem, refPricingDate })} />
                        </>
                    )}
            </MasterDataShell>
        </div >)
}

type PricingPeriod = {
    id: Guid,
    productId: Guid,
    product: string,
    channel: string,
    finalPricingFormulaIndex: string,
    finalPricingFormulaIndexName: string,
    movementDate: MovementDate,
    pricingFormula: PricingFormula,
    customerSegment: string | null,
    formulaType: string | null,
    pricingPeriodRuleId: Guid | null
    deemedDate: string | null
    refMovementDate: string | null
    refPricingDate: string | null
}

type MovementDate = 'FirstFortnight' | 'SecondFortnight' | 'FullMonth' | 'Current15ToNext14' | 'RefBy4Weeks' | 'Current1To10'
    | 'Current11To20' | 'Current21ToEndOfMonth' | 'RefBy3CD' | 'RefBy3CD4CD' | 'RefBy7CD'

export type PricingFormula = 'Last13ToLast27' | 'Last28To12' | 'FullMonth' | 'LastFullMonth' | 'SecondLastFullMonth' | 'SecondLast25ToLast24'
    | 'RefBy4Weeks' | 'ThirdLastFullMonth' | 'Last21ToLastEndOfMonth' | 'Current1To10' | 'Current11To20' | 'LastSecondFortnight'
    | 'FirstFortnight' | 'SecondFortnight' | 'Current21ToEndOfMonth' | 'Last16ToCurrent15' | 'SecondLast28ToLast27' | 'RefBy3CD'
    | 'RefBy7CD' | 'DynamicPricing'

export type FormulaType = 'Decade' | 'Monthly' | 'Fortnight' | 'Last16To15' | '3CalendarDays' | '7CalendarDays'

export let getFormulaTypes = () => {
    let formulaTypes: FormulaType[] = ['Decade', 'Fortnight', 'Monthly', 'Last16To15', '3CalendarDays', '7CalendarDays']
    return formulaTypes.map(x => ({ value: x, text: t(`admin.masterdata.pricingPeriod.formulaType.${x}`) }))
}

type ProductChannels = {
    productId: Guid,
    code: string
    channels: Channel[]
}

type Channel = {
    name: string
    customerSegments: string[]
}

let emptyPricingPeriod = (): PricingPeriod => ({
    id: guid.empty,
    productId: guid.empty,
    product: '',
    channel: '',
    finalPricingFormulaIndex: '',
    finalPricingFormulaIndexName: '',
    movementDate: 'FirstFortnight',
    pricingFormula: 'Last13ToLast27',
    customerSegment: '',
    formulaType: null,
    pricingPeriodRuleId: null,
    deemedDate: null,
    refMovementDate: null,
    refPricingDate: null
})

let toTableItem = (pricingPeriod: PricingPeriod): TableItem<MasterDataItem<PricingPeriod>> => {
    return {
        id: pricingPeriod.id,
        productId: pricingPeriod.productId,
        product: pricingPeriod.product,
        channel: pricingPeriod.channel,
        finalPricingFormulaIndex: pricingPeriod.finalPricingFormulaIndex,
        finalPricingFormulaIndexName: pricingPeriod.finalPricingFormulaIndexName,
        movementDate: pricingPeriod.movementDate,
        pricingFormula: pricingPeriod.pricingFormula,
        isModified: false,
        customerSegment: pricingPeriod.customerSegment,
        formulaType: pricingPeriod.formulaType,
        pricingPeriodRuleId: pricingPeriod.pricingPeriodRuleId,
        deemedDate: pricingPeriod.deemedDate,
        refMovementDate: pricingPeriod.refMovementDate,
        refPricingDate: pricingPeriod.refPricingDate
    }
}

type PricingPeriodFilters = {
    product: string | null,
    channel: string | null,
    finalPricingFormulaIndexName: string | null,
    customerSegment: string | null,
    movementDate: string | null,
    pricingFormula: string | null,
    formulaType: string | null,
    pricingPeriodRule: string | null
}

let noFilters: PricingPeriodFilters = {
    product: null,
    channel: null,
    finalPricingFormulaIndexName: null,
    customerSegment: null,
    movementDate: null,
    pricingFormula: null,
    formulaType: null,
    pricingPeriodRule: null
}

type PricingPeriodRule = {
    id: Guid,
    pricingPeriod: string
}

let applyFilters = (items: PricingPeriod[], filters: PricingPeriodFilters, pricingPeriodRules: PricingPeriodRule[]): PricingPeriod[] =>
    items.filter(x =>
        matchesFilter(x.product, filters.product) &&
        matchesFilter(x.channel, filters.channel) &&
        matchesFilter(x.finalPricingFormulaIndexName, filters.finalPricingFormulaIndexName) &&
        matchesFilter(x.customerSegment, filters.customerSegment) &&
        matchTranslatedValueFilter(x.movementDate, filters.movementDate, movementDateKeys) &&
        matchTranslatedValueFilter(x.pricingFormula, filters.pricingFormula, pricingFormulaKeys) &&
        matchTranslatedValueFilter(x.formulaType, filters.formulaType, formulaTypeKeys) &&
        matchPricingPeriodRuleFilter(x.pricingPeriodRuleId, filters.pricingPeriodRule, pricingPeriodRules)
    )

let matchesFilter = (value: string[] | string | number | boolean | null, filter: string | number | boolean | null): boolean => {
    if (filter === null || filter === '')
        return true
    if (Array.isArray(value))
        return value.some(item => item.toLowerCase().includes(String(filter).toLowerCase()))
    return String(value).toLowerCase().includes(String(filter).toLowerCase())
}

let matchTranslatedValueFilter = (value: string | null, filter: string | null, translationKeys: Record<string, string>): boolean => {
    if (filter === null || filter === '')
        return true

    let translationKey = translationKeys[value ?? '']
    let translation = translationKey ? t(`admin.masterdata.pricingPeriod.${translationKey}`) : value
    return (translation ?? '').toLowerCase().includes(String(filter).toLowerCase())
}

let matchPricingPeriodRuleFilter = (value: Guid | null, filter: string | null, pricingPeriodRules: PricingPeriodRule[]): boolean => {
    if (filter === null || filter === '')
        return true
    if (!value)
        return false
    let pricingPeriodRule = pricingPeriodRules.find(x => x.id === value)
    return (pricingPeriodRule?.pricingPeriod ?? '').toLowerCase().includes(String(filter).toLowerCase())
}

let movementDateKeys: Record<string, string> = {
    'FirstFortnight': 'movementDate.FirstFortnight',
    'SecondFortnight': 'movementDate.SecondFortnight',
    'FullMonth': 'movementDate.FullMonth',
    'Current15ToNext14': 'movementDate.Current15ToNext14',
    'RefBy4Weeks': 'movementDate.RefBy4Weeks',
    'Current1To10': 'movementDate.Current1To10',
    'Current11To20': 'movementDate.Current11To20',
    'Current21ToEndOfMonth': 'movementDate.Current21ToEndOfMonth',
    'RefBy3CD': 'movementDate.RefBy3CD',
    'RefBy3CD4CD': 'movementDate.RefBy3CD4CD',
    'RefBy7CD': 'movementDate.RefBy7CD'
}

let pricingFormulaKeys: Record<string, string> = {
    'Last13ToLast27': 'pricingFormula.Last13ToLast27',
    'Last28To12': 'pricingFormula.Last28To12',
    'FullMonth': 'pricingFormula.FullMonth',
    'LastFullMonth': 'pricingFormula.LastFullMonth',
    'SecondLastFullMonth': 'pricingFormula.SecondLastFullMonth',
    'SecondLast25ToLast24': 'pricingFormula.SecondLast25ToLast24',
    'RefBy4Weeks': 'pricingFormula.RefBy4Weeks',
    'ThirdLastFullMonth': 'pricingFormula.ThirdLastFullMonth',
    'Last21ToLastEndOfMonth': 'pricingFormula.Last21ToLastEndOfMonth',
    'Current1To10': 'pricingFormula.Current1To10',
    'Current11To20': 'pricingFormula.Current11To20',
    'LastSecondFortnight': 'pricingFormula.LastSecondFortnight',
    'FirstFortnight': 'pricingFormula.FirstFortnight',
    'SecondFortnight': 'pricingFormula.SecondFortnight',
    'Current21ToEndOfMonth': 'pricingFormula.Current21ToEndOfMonth',
    'Last16ToCurrent15': 'pricingFormula.Last16ToCurrent15',
    'SecondLast28ToLast27': 'pricingFormula.SecondLast28ToLast27',
    'RefBy3CD': 'pricingFormula.RefBy3CD',
    'RefBy7CD': 'pricingFormula.RefBy7CD',
    'DynamicPricing': 'pricingFormula.DynamicPricing'
}

let formulaTypeKeys: Record<string, string> = {
    'Decade': 'formulaType.Decade',
    'Fortnight': 'formulaType.Fortnight',
    'Monthly': 'formulaType.Monthly',
    'Last16To15': 'formulaType.Last16To15',
    '3CalendarDays': 'formulaType.3CalendarDays',
    '7CalendarDays': 'formulaType.7CalendarDays'
}

let styles = (theme) =>
    createStyles({
        container: { height: '100%' },
    })

export default withStyles(styles, muiOptions)(PricingPeriodMasterData)